import { useCallback, useEffect, useRef, useState } from 'react'
import { config } from '@/config'

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Canonical UI statuses — mapped from raw API strings.
 * Matches reference: '' | null → not_started, Initializing, running, finished, failed, stopped
 */
export type JobMonitoringStatus =
  | 'idle'          // before any run
  | 'connecting'    // WS connecting
  | 'initializing'  // API: "Initializing"
  | 'running'       // API: "running"
  | 'finished'      // API: "finished" | "success" | "completed"
  | 'failed'        // API: "failed" | "error"
  | 'stopped'       // API: "stopped"

/** Shape of a message received from job_monitoring_channel */
export type JobMonitoringMessage = {
  id?: number | string
  messageType?: string
  status?: string
  path?: string
  last_executed?: string
  dq_failed_records_path?: string
  total_dq_failed_records?: number | string | null
  total_dq_passed_records?: number | string | null
  isBroadcast?: boolean
  sender?: string
  [key: string]: unknown
}

/**
 * Maps raw API status strings → canonical JobMonitoringStatus.
 * Reference: pipeline-list.component.html uses these exact strings.
 */
export function mapRawStatusToJobStatus(raw: string): JobMonitoringStatus | null {
  const s = (raw ?? '').toString().toLowerCase().trim()
  switch (s) {
    case 'initializing': return 'initializing'
    case 'running': return 'running'
    case 'finished':
    case 'success':
    case 'completed': return 'finished'
    case 'failed':
    case 'error': return 'failed'
    case 'stopped': return 'stopped'
    default: return null
  }
}

const TERMINAL_STATUSES = new Set<JobMonitoringStatus>(['finished', 'failed', 'stopped'])

const WS_CHANNEL = 'job_monitoring_channel'

// ─── Hook ─────────────────────────────────────────────────────────────────────

export type UseJobMonitoringWebSocketOptions = {
  /** The data_flow_id for the current pipeline (to filter messages) */
  dataFlowId?: number | null
  /** Called for every relevant message */
  onMessage?: (msg: JobMonitoringMessage) => void
  /** Called when a terminal status is received */
  onTerminal?: (status: JobMonitoringStatus) => void
}

export type UseJobMonitoringWebSocketReturn = {
  /** Status for the current pipeline (dataFlowId) */
  jobStatus: JobMonitoringStatus
  /** Live status overrides keyed by data_flow_id — updated for ALL messages */
  liveStatuses: Record<number, JobMonitoringStatus>
  /** Dictionary of full WebSocket messages keyed by data_flow_id */
  liveMessages: Record<number, JobMonitoringMessage>
  /** Latest raw message */
  lastMessage: JobMonitoringMessage | null
  /** Start listening (call after run API succeeds) */
  startListening: () => void
  /** Disconnect and reset */
  stopListening: () => void
  /** Whether the WS is open */
  isConnected: boolean
}

export function useJobMonitoringWebSocket({
  dataFlowId,
  onMessage,
  onTerminal,
}: UseJobMonitoringWebSocketOptions = {}): UseJobMonitoringWebSocketReturn {
  const [jobStatus, setJobStatus] = useState<JobMonitoringStatus>('idle')
  const [liveStatuses, setLiveStatuses] = useState<Record<number, JobMonitoringStatus>>({})
  const [liveMessages, setLiveMessages] = useState<Record<number, JobMonitoringMessage>>({})
  const [lastMessage, setLastMessage] = useState<JobMonitoringMessage | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activeRef = useRef(false)

  const onMessageRef = useRef(onMessage)
  const onTerminalRef = useRef(onTerminal)

  useEffect(() => {
    onMessageRef.current = onMessage
    onTerminalRef.current = onTerminal
  }, [onMessage, onTerminal])

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }

  const disconnect = useCallback(() => {
    activeRef.current = false
    clearReconnectTimer()
    if (wsRef.current) {
      wsRef.current.onopen = null
      wsRef.current.onmessage = null
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close()
      }
      wsRef.current = null
    }
    setIsConnected(false)
  }, [])

  const connect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onopen = null
      wsRef.current.onmessage = null
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close()
      }
      wsRef.current = null
    }

    const base = config.adpWebSocketUrl.replace(/\/+$/, '')
    const url = `${base}/${WS_CHANNEL}`

    console.log('[JobMonitoring] Connecting to:', url)

    let ws: WebSocket
    try {
      ws = new WebSocket(url)
    } catch (err) {
      console.error('[JobMonitoring] Failed to create WebSocket:', err)
      return
    }

    wsRef.current = ws

    ws.onopen = () => {
      setIsConnected(true)
      // Don't set jobStatus to running here — wait for the first message
      console.log('[JobMonitoring] Connected')
    }

    ws.onmessage = (event) => {
      let msg: JobMonitoringMessage = {}
      try {
        msg = JSON.parse(event.data as string) as JobMonitoringMessage
      } catch {
        msg = { raw: event.data }
      }

      // Process data_flow and dq_stats messages
      if (msg.messageType !== 'data_flow' && msg.messageType !== 'dq_stats') {
        return
      }

      console.log(`[JobMonitoring] WS Message Received (${msg.messageType}):`, msg)

      setLastMessage(msg)
      onMessageRef.current?.(msg)

      // Parse status from message
      const rawStatus = (msg.status ?? '').toString()
      const mapped = mapRawStatusToJobStatus(rawStatus)

      // Update liveStatuses & liveMessages for ALL pipeline ids we receive
      const msgId = msg.id != null ? Number(msg.id) : null
      if (msgId != null) {
        if (mapped !== null) {
          setLiveStatuses(prev => ({ ...prev, [msgId]: mapped }))
        }
        setLiveMessages(prev => ({ ...prev, [msgId]: msg }))
      }

      // Update jobStatus only for the current pipeline
      if (mapped !== null) {
        const isForCurrentPipeline =
          (dataFlowId != null && msgId === dataFlowId) ||
          (dataFlowId == null && msgId == null)

        if (isForCurrentPipeline) {
          setJobStatus(mapped)

          if (TERMINAL_STATUSES.has(mapped)) {
            onTerminalRef.current?.(mapped)
            activeRef.current = false
            ws.close(1000, 'Job completed')
          }
        }
      }
    }

    ws.onclose = (event) => {
      setIsConnected(false)
      console.log('[JobMonitoring] Closed:', event.code, event.reason)
      if (activeRef.current) {
        console.log('[JobMonitoring] Reconnecting in 3s...')
        reconnectTimerRef.current = setTimeout(() => {
          if (activeRef.current) connect()
        }, 3000)
      }
    }

    ws.onerror = (err) => {
      console.error('[JobMonitoring] Error:', err)
    }
  }, [dataFlowId]) // eslint-disable-line react-hooks/exhaustive-deps

  const startListening = useCallback(() => {
    setJobStatus('idle')
    setLastMessage(null)
    activeRef.current = true
    clearReconnectTimer()
    connect()
  }, [connect])

  const stopListening = useCallback(() => {
    disconnect()
    setJobStatus('idle')
  }, [disconnect])

  useEffect(() => {
    return () => {
      activeRef.current = false
      clearReconnectTimer()
      disconnect()
    }
  }, [disconnect])

  return {
    jobStatus,
    liveStatuses,
    liveMessages,
    lastMessage,
    startListening,
    stopListening,
    isConnected,
  }
}
